import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';
import { AgentTemplateRequest, AgentTemplateResponse } from './types';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import {
  createAgentTemplate,
  deleteAgentTemplate,
  getAgentTemplate,
  getAgentTemplates,
  updateAgentTemplate
} from './api';

export function useGetAgencyAgentTemplates(
  page: number,
  options?: UseQueryOptions<{ data: AgentTemplateResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: AgentTemplateResponse[]; meta: MetaApiResponse }>({
    queryKey: ['agencyAgentTemplates', page],
    queryFn: () => getAgentTemplates(page),
    ...options
  });
}

export function useGetAgencyAgentTemplate(options?: UseQueryOptions<AgentTemplateResponse, Error>) {
  const { agentTemplateId } = useParams<{ agentTemplateId: string }>();
  return useQuery<AgentTemplateResponse>({
    queryKey: ['agencyAgentTemplate', agentTemplateId],
    queryFn: () => getAgentTemplate(agentTemplateId!),
    enabled: Boolean(agentTemplateId),
    ...options
  });
}

export function useCreateOrUpdateAgencyAgentTemplate(isModalClose?: boolean) {
  const navigate = useNavigate();
  const { agentTemplateId } = useParams<{ agentTemplateId: string }>();

  return useMutation({
    mutationFn: (payload: AgentTemplateRequest | FormData) =>
      agentTemplateId ? updateAgentTemplate(agentTemplateId!, payload) : createAgentTemplate(payload),
    onSuccess: (data) => {
      if (data !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['agencyAgentTemplate'],
          refetchType: 'active'
        });
        queryClient.invalidateQueries({
          queryKey: ['agencyAgentTemplates'],
          refetchType: 'active'
        });
        if (isModalClose) navigate(-1);
      }
    }
  });
}

export function useDeleteAgencyAgentTemplate() {
  return useMutation({
    mutationFn: (id: string) => deleteAgentTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agencyAgentTemplates'],
        refetchType: 'active'
      });
    }
  });
}
